import { apiClient } from '@lib/axios';
import { useEffect, useState } from 'react';

export interface DropdownItem { _id: string; name: string; active: boolean }
type Resource = 'courses' | 'banks';

// Module-level cache — all components share one fetch per resource per session.
const cache: Partial<Record<Resource, Promise<DropdownItem[]>>> = {};

function load(resource: Resource): Promise<DropdownItem[]> {
  if (!cache[resource]) {
    cache[resource] = apiClient
      .get(`/${resource}`, { params: { limit: 100 } })
      .then((res) => res.data.items as DropdownItem[])
      .catch((err) => {
        delete cache[resource]; // allow retry on next mount
        throw err;
      });
  }
  return cache[resource]!;
}

/** Call after a Settings mutation (add/edit/deactivate) to force a re-fetch on next mount. */
export function invalidateDropdown(resource: Resource) {
  delete cache[resource];
}

export function useDropdown(resource: Resource) {
  const [items, setItems]   = useState<DropdownItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    load(resource)
      .then((data) => {
        if (alive) { setItems(data); setError(null); }
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.error?.message ?? 'Failed to load options');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [resource]);

  return { items, loading, error };
}
